import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { formatToDate } from '../Components/jwtUtils';
import '../styles/validar-certificado.css';

function ValidarCertificado() {
    const navigate = useNavigate();
    const [certificadoId, setCertificadoId] = useState('');
    const [resultado, setResultado] = useState(null);
    const [erro, setErro] = useState(null);
    const [loading, setLoading] = useState(false);
    const [buscou, setBuscou] = useState(false);

    const handleValidar = async (e) => {
        e.preventDefault();
        if (!certificadoId.trim()) return;

        setLoading(true);
        setErro(null);
        setResultado(null);
        setBuscou(true);

        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}public/validar-certificado/${certificadoId.trim()}`
            );
            setResultado(res.data);
        } catch (error) {
            if (error.response?.status === 404) {
                setErro('Certificado não encontrado. Verifique o ID informado.');
            } else {
                setErro('Ocorreu um erro ao validar. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    const getBadgeClass = (status) => {
        if (status === 'valido') return 'valido';
        if (status === 'expirado') return 'expirado';
        return 'arquivado';
    };

    const getBadgeLabel = (status) => {
        if (status === 'valido') return 'Válido';
        if (status === 'expirado') return 'Expirado';
        return 'Arquivado';
    };

    const getValidadeTexto = (cert) => {
        if (cert.prazo_validade === null || cert.prazo_validade == 0) {
            return 'Permanente';
        }
        return `${cert.prazo_validade} ${cert.prazo_validade === 1 ? 'mês' : 'meses'}`;
    };

    return (
        <div className="validar-wrapper">
            <div className="bg-decoration">
                <div className="shape shape-1"></div>
                <div className="shape shape-2"></div>
                <div className="shape shape-3"></div>
            </div>

            <div className="validar-container">
                <div className="validar-header">
                    <button className="validar-back" onClick={() => navigate(-1)}>
                        <i className="fas fa-arrow-left"></i> Voltar
                    </button>
                    <h1 className="validar-title">Validar Certificado</h1>
                    <p className="validar-subtitle">
                        Insira o ID do certificado para verificar sua autenticidade
                    </p>
                </div>

                <div className="validar-search-card">
                    <form className="validar-search-form" onSubmit={handleValidar}>
                        <input
                            type="text"
                            className="validar-search-input"
                            placeholder="Digite o ID do certificado"
                            value={certificadoId}
                            onChange={(e) => setCertificadoId(e.target.value)}
                            required
                        />
                        <button
                            type="submit"
                            className="validar-search-btn"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" />
                                    Validando...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-search"></i> Validar
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {!buscou && !loading && (
                    <div className="validar-search-card">
                        <div className="validar-empty-state">
                            <div className="validar-empty-icon">
                                <i className="fas fa-certificate"></i>
                            </div>
                            <p className="validar-empty-text">
                                Digite o ID do certificado acima para verificar sua validade e rastreabilidade.
                            </p>
                        </div>
                    </div>
                )}

                {erro && (
                    <div className="validar-error-card">
                        <div className="validar-error-icon">
                            <i className="fas fa-times-circle"></i>
                        </div>
                        <p className="validar-error-text">{erro}</p>
                        <p className="validar-error-subtext">
                            Confira se o ID está correto e tente novamente.
                        </p>
                    </div>
                )}

                {resultado && (
                    <div className="validar-result-card">
                        <div className="validar-result-header">
                            <h3>
                                <i className="fas fa-file-certificate me-2"></i>
                                Certificado #{resultado.certificado.id}
                            </h3>
                            <span className={`validar-badge ${getBadgeClass(resultado.certificado.status)}`}>
                                {getBadgeLabel(resultado.certificado.status)}
                            </span>
                        </div>

                        <div className="validar-result-body">
                            <div className="validar-info-grid">
                                <div className="validar-info-item">
                                    <span className="validar-info-label">Aluno</span>
                                    <span className="validar-info-value">{resultado.aluno.nome}</span>
                                </div>
                                <div className="validar-info-item">
                                    <span className="validar-info-label">Matrícula</span>
                                    <span className="validar-info-value">{resultado.aluno.codigo}</span>
                                </div>
                                <div className="validar-info-item full-width">
                                    <span className="validar-info-label">Curso</span>
                                    <span className="validar-info-value">{resultado.curso.nome}</span>
                                </div>
                                <div className="validar-info-item">
                                    <span className="validar-info-label">Tópico</span>
                                    <span className="validar-info-value">{resultado.curso.topico}</span>
                                </div>
                                <div className="validar-info-item">
                                    <span className="validar-info-label">Carga Horária</span>
                                    <span className="validar-info-value">
                                        {resultado.curso.carga_horaria_total} {Number(resultado.curso.carga_horaria_total) === 1 ? 'hora' : 'horas'}
                                    </span>
                                </div>
                                {resultado.empresa && (
                                    <div className="validar-info-item">
                                        <span className="validar-info-label">Empresa</span>
                                        <span className="validar-info-value">{resultado.empresa}</span>
                                    </div>
                                )}
                                <div className="validar-info-item">
                                    <span className="validar-info-label">Tipo</span>
                                    <span className="validar-info-value">{resultado.curso.tipo || '—'}</span>
                                </div>
                                <div className="validar-info-item">
                                    <span className="validar-info-label">Data de Emissão</span>
                                    <span className="validar-info-value">
                                        {formatToDate(resultado.certificado.data_emissao)}
                                    </span>
                                </div>
                                <div className="validar-info-item">
                                    <span className="validar-info-label">Validade</span>
                                    <span className="validar-info-value">
                                        {getValidadeTexto(resultado.certificado)}
                                    </span>
                                </div>
                                {resultado.certificado.data_expiracao && (
                                    <div className="validar-info-item">
                                        <span className="validar-info-label">Expira em</span>
                                        <span className="validar-info-value">
                                            {formatToDate(resultado.certificado.data_expiracao)}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {resultado.modulos && resultado.modulos.length > 0 && (
                                <div className="validar-modulos-section">
                                    <h4 className="validar-modulos-title">
                                        <i className="fas fa-layer-group"></i> Módulos e Avaliações
                                    </h4>
                                    <table className="validar-modulos-table">
                                        <thead>
                                            <tr>
                                                <th>Módulo</th>
                                                <th>Carga Horária</th>
                                                <th>Nota</th>
                                                <th>Data da Avaliação</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {resultado.modulos.map((modulo, index) => (
                                                <tr key={index}>
                                                    <td>{modulo.nome}</td>
                                                    <td>{modulo.carga_horaria}h</td>
                                                    <td>
                                                        {modulo.nota !== null ? (
                                                            <span className={`nota-badge ${modulo.nota >= 70 ? 'aprovado' : 'reprovado'}`}>
                                                                {modulo.nota}
                                                            </span>
                                                        ) : (
                                                            <span className="nota-badge pendente">—</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {modulo.data_avaliacao
                                                            ? formatToDate(modulo.data_avaliacao)
                                                            : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ValidarCertificado;
